import React, { useEffect, useState } from "react";
import "./index.less";
import ZButton from "@atoms/ZButton";
import ZTypography from "@atoms/ZTypography";
import { EColors } from "@atoms/ZColors/interface";
import { GuidePopupProps, GuidePopupContentProps } from "./interface";
import { Card } from "antd";
import ZPopover from "@atoms/ZPopover";
import { useHighlightSpot } from "./ZHighlightMask";
import ZInput from "@molecules/ZInput";
import ZVideo from "../../ZVideo";
import { EFontWeights } from "@atoms/ZTypography/interface";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
const { ZText } = ZTypography;

const useDelayedVisibility = (visible: boolean) => {
	const [delayedVisible, setDelayedVisible] = useState(false);
	useEffect(() => {
		let timer: number;
		if (visible) {
			timer = window.setTimeout(() => {
				setDelayedVisible(visible);
			}, 500);
		} else {
			setDelayedVisible(visible);
		}
		return () => {
			window.clearTimeout(timer);
		};
	}, [visible]);
	return delayedVisible;
};

const GuidePopupContent: React.FC<GuidePopupContentProps> = ({
	title,
	description,
	note,
	buttonText,
	handleButtonClick,
	mediaUrl
}) => {
	return (
		<Card
			className="guide-popup-content"
			style={{ width: 312 }}
			cover={
				mediaUrl ? (
					<ZVideo
						id={"guide-media-" + title}
						video={mediaUrl}
						isPreview
						loop
						autoPlay
						muted
						hasLoading={true}
					/>
				) : null
			}
			bordered={false}
		>
			<div className="px-5 py-6">
				{!!title && (
					<ZText
						color={EColors.GRAY}
						colorVariant={10}
						level={1}
						weight={EFontWeights.MEDIUM}
						className="pb-1 block"
					>
						{title}
					</ZText>
				)}
				<ZText
					color={EColors.GRAY}
					colorVariant={9}
					className="block"
					level={1}
				>
					{description}
				</ZText>
				{note && (
					<ZText color={EColors.GRAY} colorVariant={7} level={1}>
						{note}
					</ZText>
				)}
			</div>
			{buttonText && (
				<div className="mx-6 mb-4 flex items-center justify-end">
					<ZButton type="primary" onClick={() => handleButtonClick()}>
						{buttonText}
					</ZButton>
				</div>
			)}
		</Card>
	);
};

export const GuidePopup: React.FC<GuidePopupProps> = ({
	title,
	description,
	note,
	placement,
	visible,
	children,
	handleClose,
	handleNext,
	highlightId,
	hasTransparentMask,
	buttonText,
	mediaUrl
}) => {
	const handleClickNext = () => {
		handleNext?.();
	};
	const handleVisbleChange = (isOpen: boolean) => {
		if (!isOpen) {
			handleClose?.();
		}
	};
	const CardContent = (
		<GuidePopupContent
			title={title ?? ""}
			buttonText={buttonText}
			description={description ?? ""}
			note={note ?? ""}
			handleButtonClick={handleClickNext}
			mediaUrl={mediaUrl}
		/>
	);

	useHighlightSpot({
		id: highlightId,
		visible,
		isTransparent: !!hasTransparentMask
	});

	const delayedVisible = useDelayedVisibility(visible);
	let width = 312;

	if (placement?.startsWith("left") || placement?.startsWith("right")) {
		width += 8;
	}

	return (
		<ZPopover
			content={CardContent}
			overlayClassName="guide-popup"
			placement={placement}
			visible={delayedVisible}
			trigger="click"
			size="rg"
			onVisibleChange={handleVisbleChange}
			overlayStyle={{ width }}
			zIndex={930}
		>
			<>{children}</>
		</ZPopover>
	);
};

const GuideTooltipContent: React.FC<GuidePopupContentProps> = ({
	title,
	description,
	buttonText,
	handleButtonClick,
	popupInputProps,
	width,
	hasInfoIcon
}) => {
	// this popupInput component only used in document editor flow
	const [inputValue, setInputValue] = useState<string>(
		popupInputProps?.defaultValue ?? ""
	);
	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setInputValue(value);
	};
	const popupInput = popupInputProps ? (
		<div className="text-form-popup-form ml-0 my-2">
			<ZInput
				defaultValue={popupInputProps?.defaultValue}
				className="text-form-input"
				onChange={handleInputChange}
				width={popupInputProps?.inputWidth}
				placeholder={popupInputProps?.placeholder}
			/>
		</div>
	) : null;

	return (
		<Card
			className="guide-tooltip-content"
			style={{ width: width ?? 272 }}
			bordered={false}
		>
			<div className="px-4 py-3">
				{!!title && (
					<ZText
						color={EColors.GRAY}
						colorVariant={1}
						level={1}
						weight={EFontWeights.MEDIUM}
						className="pb-1 block"
					>
						{title}
					</ZText>
				)}
				{!!description && (
					<div className="flex">
						{hasInfoIcon && (
							<ZIcon
								name={EIcons.INFOCIRCLE}
								size="md"
								colorVariant={1}
								className="mr-1"
							/>
						)}
						<ZText color={EColors.GRAY} colorVariant={1} level={1}>
							{description}
						</ZText>
					</div>
				)}
				{popupInput}
				{!!buttonText && (
					<div className="flex pt-1 items-center justify-end">
						<ZButton
							type="default"
							onClick={() => handleButtonClick(inputValue)}
						>
							{buttonText}
						</ZButton>
					</div>
				)}
			</div>
		</Card>
	);
};

export const GuideTooltip: React.FC<GuidePopupProps> = ({
	title,
	description,
	placement,
	visible,
	children,
	handleClose,
	buttonText,
	handleNext,
	highlightId,
	hasArrow,
	hasTransparentMask = true,
	popupInputProps,
	highlightQuery,
	width = 272,
	maskOnClickableArea = false,
	closeOnButtonClick = false,
	hasInfoIcon = false
}) => {
	const handleClickNext = (value?: string) => {
		closeOnButtonClick && handleClose?.();
		handleNext?.(value);
	};
	const handleVisbleChange = (isOpen: boolean) => {
		if (!isOpen && !closeOnButtonClick) {
			handleClose?.();
		}
	};

	const CardContent = (
		<GuideTooltipContent
			title={title ?? ""}
			buttonText={buttonText ?? ""}
			description={description ?? ""}
			handleButtonClick={handleClickNext}
			popupInputProps={popupInputProps}
			width={width}
			hasInfoIcon={hasInfoIcon}
		/>
	);

	useHighlightSpot({
		id: highlightId,
		visible,
		isTransparent: hasTransparentMask,
		query: highlightQuery,
		maskOnClickableArea
	});

	const delayedVisible = useDelayedVisibility(visible);

	//adjust width for default padding
	if (
		width &&
		(placement?.startsWith("left") || placement?.startsWith("right"))
	) {
		width += 14;
	}
	return (
		<ZPopover
			content={CardContent}
			overlayClassName="guide-tooltip"
			placement={placement}
			visible={delayedVisible}
			trigger="click"
			arrowPointAtCenter
			onVisibleChange={handleVisbleChange}
			hideArrow={!hasArrow}
			destroyTooltipOnHide
			overlayStyle={{ width }}
			zIndex={930}
		>
			<>{children}</>
		</ZPopover>
	);
};
